import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { ErrorBoundary } from "./components/site/ErrorBoundary";
import PublicLayout, { ScrollTop } from "./components/site/PublicLayout";
import AppShell from "./components/site/AppShell";
import AdminShell from "./components/site/AdminShell";
import { GuestOnly, RequireAdmin, RequireAuth } from "./components/site/Guards";
import Home from "./pages/public/Home";
import About from "./pages/public/About";
import Products from "./pages/public/Products";
import ProductDetail from "./pages/public/ProductDetail";
import HowItWorks from "./pages/public/HowItWorks";
import Contact from "./pages/public/Contact";
import FAQ from "./pages/public/FAQ";
import Legal from "./pages/public/Legal";
import { ForgotPassword, Login, Register, ResetPassword } from "./pages/public/Auth";
import Dashboard from "./pages/app/Dashboard";
import Store from "./pages/app/Store";
import Checkout from "./pages/app/Checkout";
import MockCheckout from "./pages/app/MockCheckout";
import PaymentCallback from "./pages/app/PaymentCallback";
import { PurchaseDetail, Purchases } from "./pages/app/Purchases";
import Payments from "./pages/app/Payments";
import Account from "./pages/app/Account";
import { PredictionCategory, PredictionPackage, PredictionsHub } from "./pages/app/Predictions";
import Overview from "./pages/admin/Overview";
import { UserDetail, Users } from "./pages/admin/Users";
import AdminProducts from "./pages/admin/Products";
import AdminPredictions from "./pages/admin/Predictions";
import AdminPayments from "./pages/admin/Payments";
import AdminSettings, { AdminLogs } from "./pages/admin/Settings";

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollTop />
        <ErrorBoundary>
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/:id" element={<ProductDetail />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/terms" element={<Legal doc="terms" />} />
            <Route path="/privacy" element={<Legal doc="privacy" />} />
            <Route path="/refund-policy" element={<Legal doc="refund" />} />
            <Route path="/payment-policy" element={<Legal doc="payment" />} />
          </Route>
          <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
          <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route element={<RequireAuth><AppShell /></RequireAuth>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/store" element={<Store />} />
            <Route path="/store/:id" element={<ProductDetail base="/store" />} />
            <Route path="/checkout/:type/:id" element={<Checkout />} />
            <Route path="/pay/mock/:ref" element={<MockCheckout />} />
            <Route path="/payment/callback" element={<PaymentCallback />} />
            <Route path="/purchases" element={<Purchases />} />
            <Route path="/purchases/:id" element={<PurchaseDetail />} />
            <Route path="/payments" element={<Payments />} />
            <Route path="/account" element={<Account />} />
            <Route path="/predictions" element={<PredictionsHub />} />
            <Route path="/predictions/c/:cat" element={<PredictionCategory />} />
            <Route path="/predictions/p/:id" element={<PredictionPackage />} />
          </Route>
          <Route path="/admin" element={<RequireAdmin><AdminShell /></RequireAdmin>}>
            <Route index element={<Overview />} />
            <Route path="users" element={<Users />} />
            <Route path="users/:id" element={<UserDetail />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="predictions" element={<AdminPredictions />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="logs" element={<AdminLogs />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </ErrorBoundary>
      </BrowserRouter>
      <Toaster theme="dark" position="top-center" richColors />
    </AuthProvider>
  );
}
